// components/admin/DestinationSpotManager.js
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const DestinationSpotManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [spots, setSpots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingSpot, setEditingSpot] = useState(null);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'beach',
        location: '',
        fares: { dhaka: 0, khulna: 0 },
        image: '',
        features: [],
        recommendedSeason: 'all',
        isActive: true
    });

    const [newFeature, setNewFeature] = useState('');

    useEffect(() => {
        loadSpots();
        loadStats();
    }, [filter]);

    // Search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm !== '') {
                loadSpots();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadSpots = async () => {
        try {
            setLoading(true);
            let url = '/destination-spots';
            const params = [];

            if (filter !== 'all') params.push(`category=${filter}`);
            if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const data = await apiRequest(url);
            setSpots(data.spots || []);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error loading spots: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await apiRequest('/destination-spots/admin/stats');
            setStats(data.stats);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const handleEdit = (spot) => {
        setEditingSpot(spot);
        setFormData({
            name: spot.name,
            description: spot.description || '',
            category: spot.category,
            location: spot.location,
            fares: {
                dhaka: spot.fares.dhaka,
                khulna: spot.fares.khulna
            },
            image: spot.image || '',
            features: spot.features || [],
            recommendedSeason: spot.recommendedSeason || 'all',
            isActive: spot.isActive
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (editingSpot) {
                await apiRequest(`/destination-spots/${editingSpot._id}`, 'PUT', formData);
                setMessage({ type: 'success', text: 'Spot updated successfully!' });
            } else {
                await apiRequest('/destination-spots', 'POST', formData);
                setMessage({ type: 'success', text: 'Spot created successfully!' });
            }

            setShowForm(false);
            setEditingSpot(null);
            resetForm();
            loadSpots();
            loadStats();
        } catch (error) {
            setMessage({ type: 'error', text: 'Error saving spot: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            category: 'beach',
            location: '',
            fares: { dhaka: 0, khulna: 0 },
            image: '',
            features: [],
            recommendedSeason: 'all',
            isActive: true
        });
        setNewFeature('');
    };

    const handleDelete = async (spotId, spotName) => {
        if (window.confirm(`Are you sure you want to delete "${spotName}"?`)) {
            try {
                await apiRequest(`/destination-spots/${spotId}`, 'DELETE');
                setMessage({ type: 'success', text: 'Spot deleted successfully!' });
                loadSpots();
                loadStats();
            } catch (error) {
                setMessage({ type: 'error', text: 'Error deleting spot: ' + error.message });
            }
        }
    };

    const handleToggleActive = async (spot) => {
        try {
            await apiRequest(`/destination-spots/${spot._id}/toggle`, 'PATCH');
            setMessage({
                type: 'success',
                text: `Spot ${!spot.isActive ? 'activated' : 'deactivated'} successfully!`
            });
            loadSpots();
            loadStats();
        } catch (error) {
            setMessage({ type: 'error', text: 'Error toggling spot: ' + error.message });
        }
    };

    const addFeature = () => {
        if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
            setFormData({
                ...formData,
                features: [...formData.features, newFeature.trim()]
            });
            setNewFeature('');
        }
    };

    const removeFeature = (index) => {
        const updatedFeatures = formData.features.filter((_, i) => i !== index);
        setFormData({ ...formData, features: updatedFeatures });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addFeature();
        }
    };

    const categories = [
        { value: 'all', label: 'All Categories', icon: 'fas fa-list' },
        { value: 'beach', label: 'Beach', icon: 'fas fa-umbrella-beach' },
        { value: 'mountain', label: 'Mountain', icon: 'fas fa-mountain' },
        { value: 'historical', label: 'Historical', icon: 'fas fa-landmark' },
        { value: 'adventure', label: 'Adventure', icon: 'fas fa-hiking' },
        { value: 'cultural', label: 'Cultural', icon: 'fas fa-monument' },
        { value: 'religious', label: 'Religious', icon: 'fas fa-place-of-worship' }
    ];

    const seasons = [
        { value: 'all', label: 'All Season' },
        { value: 'summer', label: 'Summer' },
        { value: 'winter', label: 'Winter' },
        { value: 'monsoon', label: 'Monsoon' }
    ];

    const handleClearSearch = () => {
        setSearchTerm('');
        loadSpots();
    };

    if (loading && !spots.length) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading destination spots...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                Destination Spots Management
                            </h4>
                            <button
                                className="btn btn-light btn-sm"
                                onClick={() => {
                                    setEditingSpot(null);
                                    resetForm();
                                    setShowForm(true);
                                }}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add New Spot
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats.total}</h4>
                                        <small>Total Spots</small>
                                    </div>
                                    <i className="fas fa-map-marker-alt fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats.active}</h4>
                                        <small>Active Spots</small>
                                    </div>
                                    <i className="fas fa-check-circle fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats.inactive}</h4>
                                        <small>Inactive Spots</small>
                                    </div>
                                    <i className="fas fa-pause-circle fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats.byCategory?.length || 0}</h4>
                                        <small>Categories</small>
                                    </div>
                                    <i className="fas fa-tags fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Filter by Category</label>
                            <select
                                className="form-select"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Search Spots</label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, description, or location..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && loadSpots()}
                                />
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={loadSpots}
                                >
                                    <i className="fas fa-search"></i>
                                </button>
                                {searchTerm && (
                                    <button
                                        className="btn btn-outline-danger"
                                        type="button"
                                        onClick={handleClearSearch}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Spots Grid */}
            <div className="row">
                {spots.map(spot => (
                    <div key={spot._id} className="col-md-6 col-lg-4 mb-4">
                        <div className={`card h-100 ${!spot.isActive ? 'border-warning' : ''}`}>
                            {spot.image && (
                                <img
                                    src={spot.image}
                                    className="card-img-top"
                                    alt={spot.name}
                                    style={{ height: '200px', objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                                    }}
                                />
                            )}
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h5 className="card-title mb-0">{spot.name}</h5>
                                    <span className={`badge bg-${spot.isActive ? 'success' : 'warning'}`}>
                                        {spot.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="mb-2">
                                    <span className="badge bg-primary me-1 text-capitalize">
                                        <i className="fas fa-tag me-1"></i>
                                        {spot.category}
                                    </span>
                                    <span className="badge bg-secondary text-capitalize">
                                        <i className="fas fa-calendar me-1"></i>
                                        {spot.recommendedSeason}
                                    </span>
                                </div>

                                <p className="card-text text-muted small">
                                    {spot.description || 'No description provided'}
                                </p>

                                <div className="mb-3">
                                    <small className="text-muted">
                                        <i className="fas fa-location-dot me-1"></i>
                                        {spot.location}
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <strong>Fares:</strong>
                                    <div className="row small mt-1">
                                        <div className="col-6">
                                            <span className="text-success">Dhaka: ৳{spot.fares.dhaka}</span>
                                        </div>
                                        <div className="col-6">
                                            <span className="text-info">Khulna: ৳{spot.fares.khulna}</span>
                                        </div>
                                    </div>
                                </div>

                                {spot.features && spot.features.length > 0 && (
                                    <div className="mb-3">
                                        <strong>Features:</strong>
                                        <div className="mt-1">
                                            {spot.features.slice(0, 3).map((feature, idx) => (
                                                <span key={idx} className="badge bg-light text-dark me-1 mb-1 small">
                                                    {feature}
                                                </span>
                                            ))}
                                            {spot.features.length > 3 && (
                                                <span className="badge bg-light text-dark small">
                                                    +{spot.features.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex gap-2 mt-3">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleEdit(spot)}
                                    >
                                        <i className="fas fa-edit me-1"></i>
                                        Edit
                                    </button>
                                    <button
                                        className={`btn btn-sm ${spot.isActive ? 'btn-warning' : 'btn-success'}`}
                                        onClick={() => handleToggleActive(spot)}
                                    >
                                        <i className={`fas ${spot.isActive ? 'fa-pause' : 'fa-play'} me-1`}></i>
                                        {spot.isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(spot._id, spot.name)}
                                    >
                                        <i className="fas fa-trash me-1"></i>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {spots.length === 0 && !loading && (
                <div className="text-center py-5">
                    <i className="fas fa-map-marker-alt fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Destination Spots Found</h5>
                    <p className="text-muted">
                        {searchTerm || filter !== 'all'
                            ? 'Try changing your search or filter criteria'
                            : 'Get started by adding your first destination spot'
                        }
                    </p>
                    {!searchTerm && filter === 'all' && (
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => {
                                setEditingSpot(null);
                                resetForm();
                                setShowForm(true);
                            }}
                        >
                            <i className="fas fa-plus me-1"></i>
                            Add First Spot
                        </button>
                    )}
                </div>
            )}

            {/* Add/Edit Form Modal */}
            {showForm && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-map-marker-alt me-2"></i>
                                    {editingSpot ? 'Edit Destination Spot' : 'Add New Destination Spot'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingSpot(null);
                                        resetForm();
                                    }}
                                    disabled={loading}
                                ></button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        {/* Basic Information */}
                                        <div className="col-md-6">
                                            <label className="form-label">Spot Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                required
                                                placeholder="Enter spot name"
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Category *</label>
                                            <select
                                                className="form-select"
                                                value={formData.category}
                                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                                required
                                                disabled={loading}
                                            >
                                                {categories.filter(cat => cat.value !== 'all').map(cat => (
                                                    <option key={cat.value} value={cat.value}>
                                                        {cat.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Location *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.location}
                                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                                required
                                                placeholder="Enter location (e.g., Cox's Bazar, Bangladesh)"
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Describe this destination spot..."
                                                disabled={loading}
                                            />
                                        </div>

                                        {/* Fares Section */}
                                        <div className="col-12">
                                            <h6 className="border-bottom pb-2">Fares from Branches</h6>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Dhaka Branch Fare (৳) *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">৳</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={formData.fares.dhaka}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        fares: { ...formData.fares, dhaka: parseInt(e.target.value) || 0 }
                                                    })}
                                                    required
                                                    min="0"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Khulna Branch Fare (৳) *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">৳</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={formData.fares.khulna}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        fares: { ...formData.fares, khulna: parseInt(e.target.value) || 0 }
                                                    })}
                                                    required
                                                    min="0"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        {/* Additional Information */}
                                        <div className="col-md-6">
                                            <label className="form-label">Recommended Season</label>
                                            <select
                                                className="form-select"
                                                value={formData.recommendedSeason}
                                                onChange={(e) => setFormData({ ...formData, recommendedSeason: e.target.value })}
                                                disabled={loading}
                                            >
                                                {seasons.map(season => (
                                                    <option key={season.value} value={season.value}>
                                                        {season.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Image URL</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={formData.image}
                                                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                                placeholder="https://example.com/image.jpg"
                                                disabled={loading}
                                            />
                                        </div>

                                        {/* Features Section */}
                                        <div className="col-12">
                                            <label className="form-label">Features</label>
                                            <div className="input-group mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newFeature}
                                                    onChange={(e) => setNewFeature(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    placeholder="Add a feature (e.g., Beach View, Swimming Pool)"
                                                    disabled={loading}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={addFeature}
                                                    disabled={loading}
                                                >
                                                    <i className="fas fa-plus"></i>
                                                </button>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {formData.features.map((feature, index) => (
                                                    <span key={index} className="badge bg-primary d-flex align-items-center">
                                                        {feature}
                                                        <button
                                                            type="button"
                                                            className="btn-close btn-close-white ms-2"
                                                            style={{ fontSize: '0.7rem' }}
                                                            onClick={() => removeFeature(index)}
                                                            disabled={loading}
                                                        ></button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="col-12">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                    disabled={loading}
                                                />
                                                <label className="form-check-label">
                                                    Active (Visible to users)
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer border-top-0 mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowForm(false);
                                                setEditingSpot(null);
                                                resetForm();
                                            }}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingSpot ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                    {editingSpot ? 'Update Spot' : 'Create Spot'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DestinationSpotManager;